import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/lib/testIds";
import { LayoutDashboard, PlusCircle, GitCompareArrows, LogOut, LogIn, Shield, Compass, Wallet, ChevronDown, Gift, Sparkles, Menu, Mail, Download, Smartphone, MoreHorizontal, Newspaper, DollarSign, ShieldCheck, Youtube, HelpCircle, PlayCircle, Share2, Calculator, Map, RotateCcw, LineChart, Bell, UserX, MessageCircle, Target, BarChart3, Coins } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ContactDialog from "@/components/ContactDialog";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import PolicyAcceptanceModal from "@/components/PolicyAcceptanceModal";
import ChatbotWidget from "@/components/ChatbotWidget";
import OnboardingTour from "@/components/OnboardingTour";
import Breadcrumbs from "@/components/Breadcrumbs";
import RelatedLinks from "@/components/RelatedLinks";
import { PAGES } from "@/lib/seoLinks";
import SiteAnnouncementBanner from "@/components/SiteAnnouncementBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const downloadAcceptanceCertificate = async () => {
    try {
      const token = localStorage.getItem("dp_token") || "";
      const r = await fetch(`${api.defaults.baseURL}/auth/acceptance-certificate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dpp-acceptance-${(user?.email || "user").split("@")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (err) {
      toast.error("Could not generate certificate");
    }
  };

  // GDPR Right-to-Portability data export. Hits `/auth/data-export`,
  // which honours the `Content-Disposition` filename the backend supplies
  // so the file lands on disk with a stable, dated name.
  const downloadDataExport = async () => {
    const t = toast.loading("Preparing your data archive…");
    try {
      const token = localStorage.getItem("dp_token") || "";
      const r = await fetch(`${api.defaults.baseURL}/auth/data-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      // Pull the filename from the response so it stays in sync with the
      // backend's naming scheme (includes the email prefix + date).
      let filename = `dpp-data-export-${(user?.email || "user").split("@")[0]}.json`;
      const dispo = r.headers.get("content-disposition") || "";
      const m = dispo.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data archive is downloading", { id: t });
    } catch (err) {
      toast.error("Could not generate data archive", { id: t });
    }
  };

  const linkCls = (path) =>
    `px-2.5 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
      loc.pathname === path
        ? "bg-primary text-primary-foreground"
        : "text-primary hover:bg-primary/10"
    }`;

  // Pre-login nav gets a darker, more saturated navy for every page-name
  // link so they read confidently against the aurora+grain backdrop. The
  // primary "Create Free Account" CTA stays bright blue and is NOT affected.
  const signedOutLinkCls = (path) =>
    `px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
      loc.pathname === path
        ? "bg-primary text-primary-foreground"
        : "text-primary hover:bg-primary/10"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Site-wide navy→amber hairline — echoes the Landing hero palette so
          every page reads as part of the same brand surface instead of
          plain app chrome. 2px, non-interactive, absolute top. */}
      <div className="site-topline" aria-hidden="true" data-testid="site-topline" />
      <ImpersonationBanner />
      <SiteAnnouncementBanner />
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border">
        <div className={`${user ? "max-w-[1600px]" : "max-w-7xl"} mx-auto px-6 py-4 flex items-center gap-4 relative`}>
          <div className="flex items-center gap-2">
            {/* Hamburger — LEFT of the logo for both mobile (always) and
                desktop when signed-out. Gives visitors a compact all-links
                menu without cluttering the trimmed 4-link top nav. */}
            <div className={user ? "md:hidden" : ""}>
              <MobileMenu user={user} onNav={nav} onContactClick={() => setContactOpen(true)} />
            </div>
            {/* Landing-page top nav is intentionally logo-less per the
                reference layout — hamburger + Features + login on either
                side maximises the hero real-estate on mobile. Every other
                route keeps the wordmark so the brand travels with the app. */}
            {loc.pathname !== "/" && (
              <Link to="/" data-testid={TEST_IDS.navHome} className="flex items-center gap-3 group shrink-0">
                {/* Logo gets an amber→navy gradient ring + soft glow in a
                    rounded wrapper so the mark feels tied to the brand
                    palette without touching the source PNG. */}
                <span className="relative inline-flex items-center justify-center shrink-0">
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-lg bg-gradient-to-br from-secondary via-primary/70 to-primary opacity-80 blur-[2px] group-hover:opacity-100 group-hover:blur-[3px] transition-all"
                  />
                  <img
                    src="/logo.png"
                    alt="Dividend Portfolio Planner"
                    className="relative h-11 w-11 rounded-lg object-cover shadow-md ring-2 ring-secondary/70 group-hover:ring-secondary transition-all"
                  />
                </span>
                <div className={`leading-tight hidden ${user ? "sm:block md:hidden 2xl:block" : "sm:block"}`}>
                  {/* Title bumped to text-primary (deep navy / amber in dark) +
                      font-semibold so it reads clearly against the aurora
                      wash on every page. */}
                  <div className="serif text-lg font-semibold tracking-tight text-primary">Dividend Portfolio Planner</div>
                  {/* Tagline — three colored action-words with financial
                      icons in place of the bullet separators. Semantic
                      meaning: Target = plan, BarChart3 = analyze, Coins =
                      income. Colors picked so each word earns a distinct
                      hue without blowing out the premium header palette. */}
                  <div
                    className="mono text-[10px] uppercase tracking-[0.22em] mt-0.5 inline-flex items-center gap-1 flex-wrap"
                    data-testid="brand-tagline"
                  >
                    <Target className="w-3 h-3 text-sky-500 dark:text-sky-400" aria-hidden="true" />
                    <span className="text-sky-600 dark:text-sky-400 font-semibold">Plan</span>
                    <BarChart3 className="w-3 h-3 ml-1 text-violet-500 dark:text-violet-400" aria-hidden="true" />
                    <span className="text-violet-600 dark:text-violet-400 font-semibold">Analyze</span>
                    <Coins className="w-3 h-3 ml-1 text-secondary" aria-hidden="true" />
                    <span className="text-secondary font-semibold">Build income</span>
                  </div>
                </div>
              </Link>
            )}
            {/* Landing-only: Features dropdown surfaces next to the hamburger
                so mobile visitors can preview every capability without opening
                the drawer. On other routes the desktop nav already carries it.
                Pricing sits right next to Features so the price story is one
                tap away — matches the desktop signed-out nav order. */}
            {!user && loc.pathname === "/" && (
              <div className="md:hidden flex items-center gap-1">
                <FeaturesMenu mobileVariant dark />
                <Link
                  to="/pricing"
                  data-testid="nav-pricing-mobile"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm font-semibold text-primary hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <DollarSign className="w-3.5 h-3.5" /> Pricing
                </Link>
              </div>
            )}
          </div>

          {/* Mobile-only centered pill — single "Create Free Account" primary
              conversion. Hidden on the landing page where Features slots into
              the logo's old spot, so the pill would overlap it; the hero CTA
              plus the mobile sticky ribbon still handle conversion. */}
          {!user && loc.pathname !== "/" && (
            <div className="md:hidden absolute left-1/2 -translate-x-1/2 flex items-center" data-testid="nav-mobile-cta-row">
              <Link
                to="/auth?mode=register"
                data-testid="nav-create-free-account-mobile"
                className="px-2.5 py-1.5 rounded-full text-[10px] mono uppercase tracking-wide whitespace-nowrap bg-[#2563eb] text-white font-semibold hover:bg-[#1d4ed8] transition"
                aria-label="Create Free Account"
              >
                Create Free Account
              </Link>
            </div>
          )}

          {/* Nav — LEFT-ALIGNED after the hamburger + logo. Sign-in stays
              right-aligned via `ml-auto` on the trailing action group. */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Signed-out header — reference-matched 3-link bar:
                Features (dropdown) · Investment 101 · Pricing.
                Log In + Create Free Account live in the trailing action group.
                Uses the darker `signedOutLinkCls` so page-name links read
                clearly against the aurora+grain backdrop. */}
            {!user && (
              <>
                <FeaturesMenu dark />
                <Link to="/how-it-works" data-testid="nav-how-it-works" className={signedOutLinkCls("/how-it-works")}>
                  Investment 101
                </Link>
                <Link
                  to="/pricing"
                  data-testid="nav-pricing"
                  className={signedOutLinkCls("/pricing") + " inline-flex items-center gap-1.5"}
                >
                  <DollarSign className="w-4 h-4" /> Pricing
                </Link>
              </>
            )}
            {user && (
              <>
                <Link to="/dashboard" data-testid={TEST_IDS.navDashboard} className={linkCls("/dashboard")}>
                  <span className="inline-flex items-center gap-2"><LayoutDashboard className="w-4 h-4" />Dashboard</span>
                </Link>
                <Link to="/holdings" data-testid={TEST_IDS.navHoldings} className={linkCls("/holdings")}>
                  <span className="inline-flex items-center gap-2"><Wallet className="w-4 h-4" />My Holdings</span>
                </Link>
                <Link to="/portfolio/new" data-testid={TEST_IDS.navNew} className={linkCls("/portfolio/new")}>
                  <span className="inline-flex items-center gap-2"><PlusCircle className="w-4 h-4" />New portfolio</span>
                </Link>
                <Link
                  to="/calculator"
                  data-testid="nav-calculator"
                  className={linkCls("/calculator") + " inline-flex items-center gap-1.5"}
                >
                  <Calculator className="w-4 h-4" /> Dividend Calculator
                </Link>
                <Link to="/research" data-testid="nav-research" className={linkCls("/research")}>
                  <span className="inline-flex items-center gap-2"><Compass className="w-4 h-4" />Research</span>
                </Link>
                {/* Compare portfolios moved into the "More" menu — the signed-in
                    workflow now leads with the actions users hit daily
                    (Dashboard → Holdings → New → Calculator → Research). Compare
                    is a one-click item under More, plus surfaced as a card on
                    the Dashboard itself. */}
                <Link to="/faq" data-testid="nav-faq" className={linkCls("/faq") + " inline-flex items-center gap-1.5"}>
                  <HelpCircle className="w-4 h-4" /> FAQ
                </Link>
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  data-testid="nav-contact-us"
                  className="px-2.5 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors text-primary hover:bg-primary/10 inline-flex items-center gap-1.5"
                >
                  <Mail className="w-4 h-4" /> Contact us
                </button>
              </>
            )}
            {user?.is_admin && (
              <Link to="/admin" data-testid="nav-admin" className={linkCls("/admin")}>
                <span className="inline-flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" />
                  Admin
                </span>
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-2 ml-auto">
            {user ? (
              <>
                {/* Signed-in users keep the full toolkit */}
                <MoreMenu
                  isDownloadActive={loc.pathname === "/download" || loc.pathname === "/install"}
                  isRoadmapActive={loc.pathname === "/coming-soon"}
                  isFaqActive={loc.pathname === "/faq"}
                  isInvestment101Active={loc.pathname === "/how-it-works"}
                  isDemoActive={loc.pathname === "/demo"}
                  isCompareActive={loc.pathname === "/compare"}
                />
                <UserMenu
                  user={user}
                  onLogout={() => { logout(); nav("/"); }}
                  onNav={nav}
                  onNewsletter={() => setNewsletterOpen(true)}
                  onCertificate={downloadAcceptanceCertificate}
                  onDataExport={downloadDataExport}
                  onDeleteAccount={() => setDeleteAccountOpen(true)}
                />
              </>
            ) : (
              // Signed-out — reference-matched: subtle "Log In" link + a
              // primary "Create Free Account" pill. Both stay right-aligned
              // via the parent ml-auto container.
              <>
                <Button
                  variant="ghost"
                  data-testid={TEST_IDS.navLogin}
                  size="sm"
                  onClick={() => nav("/auth")}
                  className="text-primary hover:bg-primary/10 font-semibold"
                >
                  <LogIn className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Log In</span>
                </Button>
                <Button
                  data-testid="nav-create-free-account"
                  size="sm"
                  onClick={() => nav("/auth?mode=register")}
                  className="rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold hidden sm:inline-flex"
                >
                  Create Free Account
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 w-full">
        {/* SEO-friendly breadcrumbs — rendered only on known public pages
            from the seoLinks catalog (anchored off `PAGES`). Hidden on the
            homepage and on auth'd app pages like /dashboard, /holdings,
            /portfolio/:id, /conversations where the extra crumb bar would
            be noise. The component renders both HTML anchors (crawlable
            in the initial HTML) and BreadcrumbList JSON-LD. */}
        {PAGES[(loc.pathname || "/").replace(/\/$/, "") || "/"] && loc.pathname !== "/" && (
          <div className="max-w-7xl mx-auto px-6 pt-4" data-testid="layout-breadcrumbs">
            <Breadcrumbs />
          </div>
        )}
        <Outlet />
        {/* Contextual Related Pages block — same catalog-driven gating as
            breadcrumbs. Builds a topic hub by auto-linking every public,
            indexable page in the current page's cluster, closing the
            "orphan page" gap and giving each page descriptive incoming
            anchors from its siblings. */}
        {PAGES[(loc.pathname || "/").replace(/\/$/, "") || "/"] && loc.pathname !== "/" && (
          <div className="max-w-7xl mx-auto px-6" data-testid="layout-related-links">
            <RelatedLinks />
          </div>
        )}
      </main>

      <footer className="border-t border-border py-6 mt-10">
        <div className="max-w-7xl mx-auto px-6 text-xs text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Dividend Portfolio Planner. Educational tool — not investment advice.</span>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {/* Order: How We Rank · Trust Center · Download App · Contact · YouTube */}
            <Link
              to="/how-we-rank"
              data-testid="footer-how-we-rank"
              className="inline-flex items-center gap-1.5 hover:text-primary transition-colors mono text-[10px] uppercase tracking-widest"
              title="How We Rank — our methodology"
            >
              <Sparkles className="w-3.5 h-3.5" /> How We Rank
            </Link>
            <Link
              to="/trust"
              data-testid="footer-trust"
              className="inline-flex items-center gap-1.5 hover:text-primary transition-colors mono text-[10px] uppercase tracking-widest"
              title="Trust Center — policies & documents"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Trust Center
            </Link>
            <Link
              to="/download"
              data-testid="footer-install"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors mono text-[10px] uppercase tracking-widest"
              title="Install DPP on your phone"
            >
              <Smartphone className="w-3.5 h-3.5" /> Download App
            </Link>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              data-testid="footer-contact"
              className="inline-flex items-center gap-1.5 hover:text-primary transition-colors mono text-[10px] uppercase tracking-widest"
              title="Contact the DPP team"
            >
              <Mail className="w-3.5 h-3.5" /> Contact
            </button>
            <a
              href="https://www.youtube.com/@DivPortplan"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-youtube"
              className="inline-flex items-center gap-1.5 hover:text-primary transition-colors mono text-[10px] uppercase tracking-widest"
              title="YouTube · @DivPortplan"
            >
              <Youtube className="w-3.5 h-3.5" /> YouTube
            </a>
            <span className="mono">Historical data via Yahoo Finance</span>
          </div>
        </div>
      </footer>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        defaultName={user?.name || ""}
        defaultEmail={user?.email || ""}
      />
      <NewsletterDialog
        open={newsletterOpen}
        onOpenChange={setNewsletterOpen}
        defaultEmail={user?.email || ""}
        defaultName={user?.name || ""}
      />
      {user && (
        <DeleteAccountDialog
          open={deleteAccountOpen}
          onOpenChange={setDeleteAccountOpen}
          user={user}
          onDeleted={() => { logout(); nav("/", { replace: true }); }}
        />
      )}
      <PolicyAcceptanceModal />
      <ChatbotWidget />
      {/* Onboarding tour — visible to all visitors (anonymous + signed in)
          so first-time visitors get oriented before they sign up. Progress
          + dismiss preference persist in localStorage. */}
      <OnboardingTour />
    </div>
  );
}


// Compact account chip in the top-right — combines the user's email + Sign out
// into one dropdown so the header stays tidy on smaller viewports. Also surfaces
// quick-nav shortcuts (Dashboard, My Holdings, Pricing) since users often reach
// for the profile menu when they lose their bearings.
function UserMenu({ user, onLogout, onNav, onNewsletter, onCertificate, onDataExport, onDeleteAccount }) {
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-user-menu"
          aria-label={`Account menu for ${user.email}`}
          className="inline-flex items-center gap-1.5 rounded-full p-1 pr-1.5 hover:bg-muted transition-colors group focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <span
            aria-hidden="true"
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center mono text-sm font-semibold uppercase shadow-sm ring-1 ring-primary/20"
          >
            {initial}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="nav-user-menu-content">
        <DropdownMenuLabel className="pb-1">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center mono text-sm font-semibold uppercase"
            >
              {initial}
            </span>
            <div className="min-w-0">
              <div className="serif text-sm truncate" data-testid="nav-user-name">
                {user.name || "Signed in"}
              </div>
              <div className="mono text-[11px] text-muted-foreground truncate" data-testid="nav-user-email">
                {user.email}
              </div>
              {user.is_admin && (
                <div className="mono text-[9px] uppercase tracking-widest text-secondary mt-0.5">
                  Admin
                </div>
              )}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onNav("/faq")} data-testid="nav-menu-faq" className="cursor-pointer">
          <HelpCircle className="w-4 h-4 mr-2" /> FAQ
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/dashboard")} data-testid="nav-menu-dashboard" className="cursor-pointer">
          <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/holdings")} data-testid="nav-menu-holdings" className="cursor-pointer">
          <Wallet className="w-4 h-4 mr-2" /> My Holdings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/conversations")} data-testid="nav-menu-conversations" className="cursor-pointer">
          <MessageCircle className="w-4 h-4 mr-2" /> My conversations
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/pricing")} data-testid="nav-menu-pricing" className="cursor-pointer">
          <Sparkles className="w-4 h-4 mr-2" /> Pricing
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/market-recap")} data-testid="nav-menu-market-recap" className="cursor-pointer">
          <Newspaper className="w-4 h-4 mr-2" /> Market Recap
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/coming-soon")} data-testid="nav-menu-roadmap" className="cursor-pointer">
          <Gift className="w-4 h-4 mr-2" /> Roadmap
        </DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem onSelect={() => onNav("/admin")} data-testid="nav-menu-admin" className="cursor-pointer">
            <Shield className="w-4 h-4 mr-2" /> Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={onNewsletter}
          data-testid="nav-menu-newsletter"
          className="cursor-pointer"
        >
          <Newspaper className="w-4 h-4 mr-2" /> Weekly newsletter
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNav("/download")} data-testid="nav-menu-download" className="cursor-pointer">
          <Smartphone className="w-4 h-4 mr-2" /> Get the app
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onNav("/trust")} data-testid="nav-menu-trust" className="cursor-pointer">
          <ShieldCheck className="w-4 h-4 mr-2" /> Trust Center
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onCertificate} data-testid="nav-menu-certificate" className="cursor-pointer">
          <Download className="w-4 h-4 mr-2" /> Acceptance certificate
        </DropdownMenuItem>
        {/* GDPR Article 20 · CCPA § 1798.100 — one-click data-portability
            export. Triggers a fresh archive download every time; nothing
            is cached client-side so admins can't stale-serve older data. */}
        <DropdownMenuItem
          onSelect={onDataExport}
          data-testid="nav-menu-data-export"
          className="cursor-pointer"
        >
          <Download className="w-4 h-4 mr-2" /> Download my data
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="nav-menu-youtube" className="cursor-pointer">
          <a href="https://www.youtube.com/@DivPortplan" target="_blank" rel="noopener noreferrer">
            <Youtube className="w-4 h-4 mr-2" /> YouTube channel
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onLogout}
          data-testid={TEST_IDS.navLogout}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </DropdownMenuItem>
        {/* Apple App Store guideline 5.1.1(v) + GDPR Right to Erasure —
            in-app account deletion must be reachable from a persistent
            spot. The DeleteAccountDialog handles password re-auth for
            password accounts and email-confirmation for OAuth accounts. */}
        <DropdownMenuItem
          onSelect={(e) => { e?.preventDefault?.(); onDeleteAccount(); }}
          data-testid="nav-menu-delete-account"
          className="cursor-pointer text-destructive/80 focus:text-destructive"
        >
          <UserX className="w-4 h-4 mr-2" /> Delete my account
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Signed-out desktop dropdown that surfaces the app's core capabilities in
// plain English. Each row is a one-click deep-link so anonymous visitors
// can preview any feature without hunting through the sitemap. Kept flat
// (no nested submenus) so the menu is scannable in under 2 seconds.
function FeaturesMenu({ mobileVariant = false, dark = false }) {
  const items = [
    { to: "/portfolio/new",       icon: PlusCircle,       title: "Plan a Portfolio",     desc: "Build any mix of stocks or ETFs and set target weights" },
    { to: "/portfolio/new",       icon: LineChart,        title: "Backtest with Real Data", desc: "Rewind 30 years of prices and dividends, DRIP on or off" },
    { to: "/calculator",          icon: Calculator,       title: "Dividend Calculator",  desc: "Project future dividend income with tax and inflation" },
    { to: "/research",            icon: Compass,          title: "Research & AI Analysis", desc: "AI thesis, safety score and one-click deep-dive per ticker" },
    { to: "/holdings",            icon: Wallet,           title: "Track Live Holdings",  desc: "Broker sync via SnapTrade — Roth, Taxable, 401k, IRA" },
    { to: "/vs",                  icon: GitCompareArrows, title: "Compare Apps",         desc: "See exactly how DPP stacks up against every competitor" },
    { to: "/market-recap",        icon: Newspaper,        title: "Market Recap",         desc: "Weekly dividend digest + ad-hoc news, free to read" },
    { to: "/download",            icon: Bell,             title: "Ex-Dividend Alerts",   desc: "Get a heads-up the day before each payment lands" },
    { to: "/demo",                icon: PlayCircle,       title: "30-Second Demo",       desc: "Watch a Pro user's workflow with no sign-up required" },
  ];
  // Compact icon-only trigger on mobile (used only on the landing page's
  // logo-less nav), full-word trigger on desktop. The `dark` variant is
  // used in the pre-login header so "Features" matches the darker nav
  // treatment on the other page-name links while the Create Free Account
  // CTA keeps its bright blue.
  const triggerCls = mobileVariant
    ? (dark
        ? "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm font-semibold text-primary hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
        : "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm text-foreground/80 hover:text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30")
    : (dark
        ? "hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-primary hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
        : "hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-foreground/70 hover:text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={mobileVariant ? "nav-features-mobile" : "nav-features"}
        className={triggerCls}
      >
        Features <ChevronDown className="w-3.5 h-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80" data-testid="nav-features-menu-content">
        <DropdownMenuLabel className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Everything you can do with DPP
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem
              key={item.title}
              asChild
              className="cursor-pointer py-2.5"
              data-testid={`nav-features-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Link to={item.to} className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0 mt-0.5">
                  <Icon className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground leading-tight">{item.title}</span>
                  <span className="block text-xs text-muted-foreground leading-tight mt-0.5">{item.desc}</span>
                </span>
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


// Mobile-only hamburger menu — sits next to the logo, shows every nav route
// in a single dropdown so the header stays compact on phones.
function MobileMenu({ user, onNav, onContactClick }) {
  // "Get Started" leads both lists — it is the highest-value orientation
  // action for first-time visitors and returning users who want a refresher.
  // `action: "tour"` tells the click handler below to re-summon the tour
  // instead of navigating to a route.
  const publicItems = [
    { action: "tour", label: "Get Started", icon: Map },
    { action: "reset-tour", label: "Reset Get Started tour", icon: RotateCcw },
    { to: "/how-we-rank", label: "How We Rank", icon: Sparkles },
    { to: "/research", label: "Research", icon: Compass },
    { to: "/how-it-works", label: "Investment 101", icon: Sparkles },
    { to: "/demo", label: "See the demo", icon: PlayCircle },
    { to: "/faq", label: "FAQ", icon: HelpCircle },
    { to: "/pricing", label: "Pricing", icon: Sparkles },
    { to: "/coming-soon", label: "Roadmap", icon: Gift },
    { to: "/vs", label: "Compare apps", icon: GitCompareArrows },
    { to: "/affiliate", label: "Become an affiliate", icon: Share2 },
    { to: "/download", label: "Download", icon: Download },
  ];
  const userItems = [
    { action: "tour", label: "Get Started", icon: Map },
    { action: "reset-tour", label: "Reset Get Started tour", icon: RotateCcw },
    { to: "/portfolio/new", label: "New portfolio", icon: PlusCircle },
    { to: "/calculator", label: "Dividend Calculator", icon: Calculator },
    { to: "/compare", label: "Compare", icon: GitCompareArrows },
    { to: "/research", label: "Research", icon: Compass },
    { to: "/how-it-works", label: "Investment 101", icon: Sparkles },
    { to: "/demo", label: "See the demo", icon: PlayCircle },
    { to: "/vs", label: "Compare apps", icon: GitCompareArrows },
    { to: "/affiliate", label: "Become an affiliate", icon: Share2 },
  ];
  const items = user ? userItems : publicItems;

  const handleItem = (item) => {
    if (item.action === "tour") {
      try { window.dispatchEvent(new CustomEvent("dp:open-tour")); } catch {}
      return;
    }
    if (item.action === "reset-tour") {
      try { window.dispatchEvent(new CustomEvent("dp:reset-tour")); } catch {}
      return;
    }
    onNav(item.to);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="nav-mobile-menu"
          aria-label="Open navigation menu"
          className="inline-flex items-center justify-center rounded-md border border-border bg-card hover:border-primary/40 transition-colors w-9 h-9 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <Menu className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" data-testid="nav-mobile-menu-content">
        <DropdownMenuLabel className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Navigate
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((item) => {
          const Icon = item.icon;
          const key = item.action ? `action-${item.action}` : item.to + item.label;
          const testId = item.action
            ? `mobile-nav-action-${item.action}`
            : `mobile-nav-${item.to.replace(/[/]/g, "-")}`;
          return (
            <DropdownMenuItem
              key={key}
              onSelect={() => handleItem(item)}
              className="cursor-pointer"
              data-testid={testId}
            >
              <Icon className="w-4 h-4 mr-2" /> {item.label}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onContactClick}
          className="cursor-pointer"
          data-testid="mobile-nav-contact"
        >
          <Mail className="w-4 h-4 mr-2" /> Contact us
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


// ---------- Grouped "More" dropdown (pre-login): Roadmap · FAQ · Download · YouTube ----------
function MoreMenu({ isDownloadActive, isRoadmapActive, isFaqActive, isInvestment101Active, isDemoActive, isCompareActive }) {
  const anyActive = isDownloadActive || isRoadmapActive || isFaqActive || isInvestment101Active || isDemoActive || isCompareActive;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="nav-more"
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
          anyActive ? "bg-primary text-primary-foreground" : "text-primary hover:bg-primary/10"
        }`}
      >
        <MoreHorizontal className="w-4 h-4" /> More
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {/* Signed-in "More" — deduplicated against UserMenu (FAQ / Roadmap /
            Download / YouTube live there). Features + only content-and-context
            items unique to the top bar remain here. */}
        <DropdownMenuItem asChild data-testid="more-menu-features">
          <Link to="/#" onClick={(e) => e.preventDefault()} className="cursor-pointer opacity-60 pointer-events-none">
            <Sparkles className="w-4 h-4 mr-2" /> Features ▾
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-plan-portfolio">
          <Link to="/portfolio/new" className="cursor-pointer pl-8">
            <PlusCircle className="w-4 h-4 mr-2" /> Plan a Portfolio
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-backtest">
          <Link to="/portfolio/new" className="cursor-pointer pl-8">
            <LineChart className="w-4 h-4 mr-2" /> Backtest with Real Data
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-calculator">
          <Link to="/calculator" className="cursor-pointer pl-8">
            <Calculator className="w-4 h-4 mr-2" /> Dividend Calculator
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-research">
          <Link to="/research" className="cursor-pointer pl-8">
            <Compass className="w-4 h-4 mr-2" /> Research & AI Analysis
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="more-menu-how-it-works">
          <Link to="/how-it-works" className="cursor-pointer">
            <Sparkles className="w-4 h-4 mr-2" /> Investment 101
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-demo">
          <Link to="/demo" className="cursor-pointer">
            <PlayCircle className="w-4 h-4 mr-2" /> Demo
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="more-menu-compare-portfolios">
          <Link to="/compare" className="cursor-pointer">
            <GitCompareArrows className="w-4 h-4 mr-2" /> Compare Portfolios
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-compare-apps">
          <Link to="/vs" className="cursor-pointer">
            <GitCompareArrows className="w-4 h-4 mr-2" /> Compare apps
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="more-menu-affiliate">
          <Link to="/affiliate" className="cursor-pointer">
            <Share2 className="w-4 h-4 mr-2" /> Become an affiliate
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------- Newsletter subscribe icon (opens a small dialog) ----------
function NewsletterButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="nav-newsletter"
      title="Subscribe to the DPP weekly newsletter"
      aria-label="Subscribe to the DPP weekly newsletter"
      className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full text-foreground/60 hover:text-primary hover:bg-primary/10 transition-colors relative"
    >
      <Newspaper className="w-4 h-4" />
      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-secondary" aria-hidden="true" />
    </button>
  );
}

function NewsletterDialog({ open, onOpenChange, defaultEmail = "", defaultName = "" }) {
  const [email, setEmail] = React.useState(defaultEmail);
  const [name, setName]   = React.useState(defaultName);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone]   = React.useState(false);
  const toastRef = React.useRef(null);

  React.useEffect(() => {
    if (open) { setEmail(defaultEmail); setName(defaultName); setDone(false); }
  }, [open, defaultEmail, defaultName]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const em = email.trim();
    if (!em || !em.includes("@")) { return; }
    setSubmitting(true);
    try {
      const api = (await import("@/lib/api")).default;
      const { toast } = await import("sonner");
      toastRef.current = toast;
      await api.post("/newsletter/subscribe", { email: em, name: name.trim() || undefined });
      toast.success("You're on the list — first digest lands Saturday.");
      setDone(true);
      setTimeout(() => onOpenChange(false), 1500);
    } catch (err) {
      if (toastRef.current) toastRef.current.error(err?.response?.data?.detail || "Subscribe failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="newsletter-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl inline-flex items-center gap-2"><Newspaper className="w-5 h-5 text-primary" /> The DPP Weekly</DialogTitle>
          <DialogDescription>
            A Saturday-morning digest with what to try, one Investment-101 concept, and the tickers going ex-dividend in the week ahead. One-click unsubscribe in every email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs mono uppercase tracking-widest text-muted-foreground">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
              data-testid="newsletter-email"
            />
          </div>
          <div>
            <label className="text-xs mono uppercase tracking-widest text-muted-foreground">Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane"
              maxLength={120}
              className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
              data-testid="newsletter-name"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="newsletter-cancel">Not now</Button>
            <Button type="submit" disabled={submitting || done} className="rounded-full" data-testid="newsletter-submit">
              {done ? "Subscribed" : submitting ? "Subscribing…" : "Subscribe"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

